import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PayoutSetupForm from "../components/PayoutSetupForm";
import {
  ArrowLeft,
  Landmark,
  Loader2,
  CheckCircle2,
  Clock,
  KeyRound,
  Copy,
  ShieldCheck,
  Unplug,
  RefreshCw,
} from "lucide-react";

const EMPTY_FORM = {
  beneficiary_name: "",
  account_number: "",
  confirm_account_number: "",
  ifsc: "",
  phone: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  postal_code: "",
};

const EMPTY_GATEWAY_FORM = {
  key_id: "",
  key_secret: "",
  webhook_secret: "",
};

const INPUT_CLASS =
  "h-11 rounded-md border-zinc-200 focus-visible:ring-black";

function StatusBadge({ status }) {
  const activated = status === "activated";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
        activated
          ? "bg-green-50 text-green-700 border border-green-200"
          : "bg-amber-50 text-amber-700 border border-amber-200"
      }`}
      data-testid="payout-status-badge"
    >
      {activated ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : (
        <Clock className="w-3.5 h-3.5" />
      )}
      {activated ? "Active — payouts enabled" : `Status: ${status || "pending"}`}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function randomWebhookSecret() {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(32);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

async function copyText(value, label) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy. Select the text and copy it manually.");
  }
}

function CopyField({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs text-zinc-500 mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 min-w-0 truncate text-sm bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2"
          data-testid={testId}
        >
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          className="h-9 rounded-md border-zinc-200 shrink-0"
          onClick={() => copyText(value, label)}
          aria-label={`Copy ${label}`}
        >
          <Copy className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * "Bring your own gateway": the organizer connects their own Razorpay account
 * so attendee payments settle directly to them. The platform never holds the
 * money; its commission is invoiced separately.
 */
function GatewaySection({ gateway, onChange }) {
  const [form, setForm] = useState(EMPTY_GATEWAY_FORM);
  const [editing, setEditing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const connected = Boolean(gateway?.connected);
  const showForm = !connected || editing;
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const webhookEvents = gateway?.webhook_events?.length
    ? gateway.webhook_events
    : ["payment.captured"];

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      const { data } = await api.post("/organizer/gateway", {
        key_id: form.key_id.trim(),
        key_secret: form.key_secret.trim(),
        webhook_secret: form.webhook_secret.trim(),
      });
      onChange(data);
      setForm(EMPTY_GATEWAY_FORM);
      setEditing(false);
      toast.success(
        "Razorpay account connected. New ticket payments now settle directly to you."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConnecting(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/organizer/gateway/validate", {});
      toast.success(`Razorpay accepted your keys (${data.key_id_masked}).`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      const { data } = await api.delete("/organizer/gateway");
      onChange(data);
      setConfirmDisconnect(false);
      setEditing(false);
      toast.success(
        "Razorpay account disconnected. New payments go through Growithevent again."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDisconnecting(false);
    }
  };

  if (gateway && !gateway.available) {
    return (
      <section className="mb-10" data-testid="gateway-section">
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-zinc-100 rounded-md flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5 text-zinc-600" />
            </div>
            <div>
              <p className="font-medium mb-1">
                Connecting your own Razorpay account is not enabled yet
              </p>
              <p className="text-sm text-zinc-500 leading-relaxed">
                Until it is, payments are collected through Growithevent and
                settled to you manually after the event.
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-10" data-testid="gateway-section">
      <div className="mb-5">
        <p className="overline mb-2">Your Razorpay account</p>
        <h2 className="font-heading text-2xl font-semibold tracking-tight mb-2">
          Get paid directly
        </h2>
        <p className="text-zinc-500 max-w-xl text-sm leading-relaxed">
          Connect your own Razorpay account and every ticket payment settles
          straight into it — Growithevent never holds your money. Fees are 5%
          per ticket: Razorpay deducts its 2% automatically, and Growithevent's
          3% is invoiced to you after the event.
        </p>
      </div>

      {connected && (
        <div
          className="bg-white border border-zinc-200 rounded-md p-6 mb-6"
          data-testid="gateway-connected-card"
        >
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-md flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <p className="font-medium" data-testid="gateway-key-id">
                  Key {gateway.key_id_masked}
                </p>
                <p className="text-sm text-zinc-500">
                  Connected {formatDate(gateway.connected_at)}
                  {gateway.validated_at &&
                    ` · last verified ${formatDate(gateway.validated_at)}`}
                </p>
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200"
              data-testid="gateway-status-badge"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Connected — payments settle to you
            </span>
          </div>

          {gateway.key_env === "test" && (
            <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              These are <strong>test mode</strong> keys. Attendees will not be
              charged real money until you connect live keys.
            </p>
          )}

          <div className="mt-5 grid gap-4">
            <CopyField
              label="Webhook URL (set in Razorpay → Settings → Webhooks)"
              value={gateway.webhook_url}
              testId="gateway-webhook-url"
            />
            <p className="text-xs text-zinc-500">
              Active events: <code>{webhookEvents.join(", ")}</code> · Secret:{" "}
              {gateway.has_webhook_secret
                ? "saved (use the same one in the Razorpay webhook form)"
                : "not set"}
            </p>
          </div>

          {!confirmDisconnect ? (
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={testConnection}
                disabled={testing}
                className="rounded-md border-zinc-200"
                data-testid="gateway-test-button"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                )}
                Test connection
              </Button>
              {!editing && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="rounded-md border-zinc-200"
                  data-testid="gateway-edit-button"
                >
                  <KeyRound className="w-4 h-4 mr-1.5" />
                  Update keys
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDisconnect(true)}
                className="rounded-md border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="gateway-disconnect-button"
              >
                <Unplug className="w-4 h-4 mr-1.5" />
                Disconnect
              </Button>
            </div>
          ) : (
            <div
              className="mt-5 border border-red-200 bg-red-50 rounded-md p-4"
              data-testid="gateway-disconnect-confirm"
            >
              <p className="text-sm text-red-800 mb-3">
                Disconnect your Razorpay account? New payments will be collected
                through Growithevent and settled to you manually. Attendees who
                are mid-checkout right now may need to retry.
              </p>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  onClick={disconnect}
                  disabled={disconnecting}
                  className="h-9 bg-red-600 text-white hover:bg-red-700 rounded-md"
                  data-testid="gateway-disconnect-confirm-button"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : null}
                  Yes, disconnect
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmDisconnect(false)}
                  className="h-9 rounded-md border-zinc-200"
                >
                  Keep connected
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={connect}
          className="bg-white border border-zinc-200 rounded-md p-6 space-y-5"
          data-testid="gateway-form"
        >
          <ol className="text-sm text-zinc-600 space-y-2 list-decimal pl-5">
            <li>
              In your Razorpay dashboard open{" "}
              <span className="font-medium text-zinc-900">
                Account &amp; Settings → API Keys
              </span>{" "}
              and generate a key. Paste the Key ID and Key Secret below.
            </li>
            <li>
              Open{" "}
              <span className="font-medium text-zinc-900">
                Account &amp; Settings → Webhooks → Add new webhook
              </span>
              . Use the URL below, tick{" "}
              <code>{webhookEvents.join(", ")}</code>, and set the same webhook
              secret you enter here.
            </li>
            <li>Connect. We verify the keys with Razorpay before saving them.</li>
          </ol>

          {gateway?.webhook_url && (
            <CopyField
              label="Webhook URL"
              value={gateway.webhook_url}
              testId="gateway-webhook-url-form"
            />
          )}

          <div>
            <Label className="text-sm font-medium mb-2 block">Key ID</Label>
            <Input
              required
              value={form.key_id}
              onChange={set("key_id")}
              placeholder="rzp_live_…"
              autoComplete="off"
              className={INPUT_CLASS}
              data-testid="gateway-key-id-input"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">Key Secret</Label>
            <Input
              required
              type="password"
              value={form.key_secret}
              onChange={set("key_secret")}
              placeholder="Shown once by Razorpay when you generate the key"
              autoComplete="new-password"
              className={INPUT_CLASS}
              data-testid="gateway-key-secret-input"
            />
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">
              Webhook secret
            </Label>
            <div className="flex items-center gap-2">
              <Input
                required
                minLength={8}
                value={form.webhook_secret}
                onChange={set("webhook_secret")}
                placeholder="Any secret you choose — enter the same one in Razorpay"
                autoComplete="off"
                className={INPUT_CLASS}
                data-testid="gateway-webhook-secret-input"
              />
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-md border-zinc-200 shrink-0"
                onClick={() =>
                  setForm({ ...form, webhook_secret: randomWebhookSecret() })
                }
                data-testid="gateway-generate-secret-button"
              >
                Generate
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-md border-zinc-200 shrink-0"
                disabled={!form.webhook_secret}
                onClick={() => copyText(form.webhook_secret, "Webhook secret")}
                aria-label="Copy webhook secret"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={connecting}
              className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-6"
              data-testid="gateway-connect-button"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying with Razorpay…
                </>
              ) : connected ? (
                "Save new keys"
              ) : (
                "Connect Razorpay account"
              )}
            </Button>
            {connected && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setForm(EMPTY_GATEWAY_FORM);
                }}
                className="h-11 rounded-md border-zinc-200"
              >
                Cancel
              </Button>
            )}
          </div>
          <p className="text-xs text-zinc-400">
            Your secrets are encrypted before they are stored and are never
            shown again. Growithevent uses them only to create orders, verify
            payments and issue refunds on your account.
          </p>
        </form>
      )}
    </section>
  );
}

export default function PayoutSettings() {
  const [current, setCurrent] = useState(null);
  const [gateway, setGateway] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("setup");

  const load = async () => {
    try {
      const [payoutRes, gatewayRes] = await Promise.allSettled([
        api.get("/organizer/payout"),
        api.get("/organizer/gateway"),
      ]);
      if (payoutRes.status === "fulfilled") {
        setCurrent(payoutRes.value.data);
        if (!payoutRes.value.data.configured) setEditing(true);
      } else {
        toast.error(formatApiError(payoutRes.reason));
      }
      if (gatewayRes.status === "fulfilled") {
        setGateway(gatewayRes.value.data);
      } else {
        toast.error(formatApiError(gatewayRes.reason));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.account_number !== form.confirm_account_number) {
      toast.error("Account numbers do not match");
      return;
    }
    setSaving(true);
    try {
      const { confirm_account_number, ...payload } = form;
      const { data } = await api.post("/organizer/payout", {
        ...payload,
        ifsc: payload.ifsc.toUpperCase().trim(),
        account_number: payload.account_number.trim(),
      });
      setCurrent(data);
      setEditing(false);
      setForm(EMPTY_FORM);
      toast.success("Bank account saved. Payouts will go to this account.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const ownGateway = Boolean(gateway?.connected);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/organizer/dashboard">
            <Button variant="outline" className="rounded-md border-zinc-200 h-9">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
            </Button>
          </Link>
          <span className="font-heading font-semibold tracking-tight">
            Payout settings
          </span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="overline mb-3">Payouts</p>
          <h1 className="font-heading text-4xl font-semibold tracking-tighter mb-3">
            How you get paid
          </h1>
          <p className="text-zinc-500 max-w-xl">
            {ownGateway
              ? "Ticket payments settle directly into your own Razorpay account. Growithevent invoices its commission separately."
              : "Send us your details under Payout setup and we'll open a Razorpay account in your name, so ticket money goes straight to your bank. Already have Razorpay keys? Connect them yourself instead."}
          </p>
        </div>

        <div
          className="mb-8 inline-flex rounded-lg border border-zinc-200 bg-white p-1"
          role="tablist"
          data-testid="payout-tabs"
        >
          {[
            { key: "setup", label: "Payout setup" },
            { key: "keys", label: "I have Razorpay keys" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              onClick={() => setTab(item.key)}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                tab === item.key ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"
              }`}
              data-testid={`payout-tab-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {tab === "setup" && <PayoutSetupForm gatewayConnected={ownGateway} />}

        {tab === "keys" && loading && <p className="text-zinc-500">Loading…</p>}
        {tab === "keys" && !loading && (
          <>
            <GatewaySection gateway={gateway} onChange={setGateway} />

            {!ownGateway && (
              <section data-testid="bank-section">
                <div className="mb-5">
                  <p className="overline mb-2">Manual settlement</p>
                  <h2 className="font-heading text-2xl font-semibold tracking-tight">
                    Your bank account
                  </h2>
                </div>
                {current && !current.available ? (
                  <div className="bg-white border border-zinc-200 rounded-md p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-zinc-100 rounded-md flex items-center justify-center shrink-0">
                        <Landmark className="w-5 h-5 text-zinc-600" />
                      </div>
                      <div>
                        <p className="font-medium mb-1">
                          Route transfers are not active yet
                        </p>
                        <p className="text-sm text-zinc-500 leading-relaxed">
                          Route transfers are disabled until the regulatory and Razorpay setup is complete. Settlements are processed manually after the event and any refund/dispute window, according to the host agreement.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {current?.configured && (
                      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-zinc-100 rounded-md flex items-center justify-center">
                              <Landmark className="w-5 h-5 text-zinc-600" />
                            </div>
                            <div>
                              <p className="font-medium" data-testid="payout-beneficiary">
                                {current.beneficiary_name}
                              </p>
                              <p className="text-sm text-zinc-500">
                                A/C ••••{current.account_last4} · {current.ifsc}
                              </p>
                            </div>
                          </div>
                          <StatusBadge status={current.activation_status} />
                        </div>
                        {!editing && (
                          <Button
                            variant="outline"
                            onClick={() => setEditing(true)}
                            className="mt-4 rounded-md border-zinc-200"
                            data-testid="edit-payout-button"
                          >
                            Update bank account
                          </Button>
                        )}
                      </div>
                    )}

                    {editing && (
                      <form
                        onSubmit={submit}
                        className="bg-white border border-zinc-200 rounded-md p-6 space-y-5"
                        data-testid="payout-form"
                      >
                        <div>
                          <Label className="text-sm font-medium mb-2 block">
                            Account holder name
                          </Label>
                          <Input
                            required
                            minLength={4}
                            value={form.beneficiary_name}
                            onChange={set("beneficiary_name")}
                            placeholder="Name exactly as per bank records"
                            className={INPUT_CLASS}
                            data-testid="payout-name-input"
                          />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label className="text-sm font-medium mb-2 block">
                              Account number
                            </Label>
                            <Input
                              required
                              inputMode="numeric"
                              value={form.account_number}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  account_number: e.target.value.replace(/\D/g, ""),
                                })
                              }
                              placeholder="Bank account number"
                              className={INPUT_CLASS}
                              data-testid="payout-account-input"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium mb-2 block">
                              Confirm account number
                            </Label>
                            <Input
                              required
                              inputMode="numeric"
                              value={form.confirm_account_number}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  confirm_account_number: e.target.value.replace(/\D/g, ""),
                                })
                              }
                              placeholder="Re-enter account number"
                              className={INPUT_CLASS}
                              data-testid="payout-account-confirm-input"
                            />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label className="text-sm font-medium mb-2 block">IFSC</Label>
                            <Input
                              required
                              minLength={11}
                              maxLength={11}
                              value={form.ifsc}
                              onChange={(e) =>
                                setForm({ ...form, ifsc: e.target.value.toUpperCase() })
                              }
                              placeholder="e.g. HDFC0001234"
                              className={`${INPUT_CLASS} uppercase`}
                              data-testid="payout-ifsc-input"
                            />
                          </div>
                          <div>
                            <Label className="text-sm font-medium mb-2 block">
                              Phone number
                            </Label>
                            <Input
                              required
                              type="tel"
                              value={form.phone}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                                })
                              }
                              placeholder="10-digit mobile number"
                              className={INPUT_CLASS}
                              data-testid="payout-phone-input"
                            />
                          </div>
                        </div>

                        <div className="pt-2 border-t border-zinc-100">
                          <p className="text-sm font-medium mb-4 mt-2">
                            Registered address
                            <span className="text-zinc-400 font-normal">
                              {" "}
                              (required by Razorpay for payouts)
                            </span>
                          </p>
                          <div className="space-y-5">
                            <Input
                              required
                              value={form.street1}
                              onChange={set("street1")}
                              placeholder="Address line 1"
                              className={INPUT_CLASS}
                              data-testid="payout-street1-input"
                            />
                            <Input
                              value={form.street2}
                              onChange={set("street2")}
                              placeholder="Address line 2 (optional)"
                              className={INPUT_CLASS}
                            />
                            <div className="grid sm:grid-cols-3 gap-5">
                              <Input
                                required
                                value={form.city}
                                onChange={set("city")}
                                placeholder="City"
                                className={INPUT_CLASS}
                                data-testid="payout-city-input"
                              />
                              <Input
                                required
                                value={form.state}
                                onChange={set("state")}
                                placeholder="State"
                                className={INPUT_CLASS}
                                data-testid="payout-state-input"
                              />
                              <Input
                                required
                                minLength={6}
                                maxLength={6}
                                inputMode="numeric"
                                value={form.postal_code}
                                onChange={(e) =>
                                  setForm({
                                    ...form,
                                    postal_code: e.target.value.replace(/\D/g, ""),
                                  })
                                }
                                placeholder="PIN code"
                                className={INPUT_CLASS}
                                data-testid="payout-pin-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 pt-2">
                          <Button
                            type="submit"
                            disabled={saving}
                            className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-6"
                            data-testid="save-payout-button"
                          >
                            {saving ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving…
                              </>
                            ) : (
                              "Save bank account"
                            )}
                          </Button>
                          {current?.configured && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setEditing(false)}
                              className="h-11 rounded-md border-zinc-200"
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-zinc-400">
                          Your details are sent securely to Razorpay to create your
                          payout account. Growithevent stores only the last 4 digits of
                          your account number.
                        </p>
                      </form>
                    )}
                  </>
                )}
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
